import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Upload, Trash2, Mail, ImagePlus, Loader2, CheckCheck, Car } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, absUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LOCATIONS = ["Denver", "Mooresville", "Cornelius", "Huntersville", "Davidson", "Sherrills Ford"];

function TeamManager() {
  const [team, setTeam] = useState([]);
  const [uploadingSlug, setUploadingSlug] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/team");
    setTeam(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (slug, file) => {
    if (!file) return;
    setUploadingSlug(slug);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/team/${slug}/photo`, { photo_url: up.data.url });
      await load();
      toast.success("Photo updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingSlug(null);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {team.map((m) => (
        <div key={m.slug} className="card p-6" data-testid={`admin-team-${m.slug}`}>
          <div className="flex items-start gap-5">
            <div className="w-28 h-28 rounded-2xl overflow-hidden bg-[#F0EBE1] shrink-0">
              {m.photo_url ? <img src={absUrl(m.photo_url)} alt={m.name} className="w-full h-full object-cover" /> : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase font-bold tracking-widest text-[#21C078]">{m.title}</div>
              <h3 className="text-lg font-black">{m.name}</h3>
              <p className="mt-1 text-sm text-[#1C1C1C]/70 line-clamp-3">{m.bio}</p>
              <label className="mt-3 inline-flex items-center gap-2 cursor-pointer btn-pill-ghost !py-2 !px-4 text-sm" data-testid={`admin-team-upload-${m.slug}`}>
                {uploadingSlug === m.slug ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploadingSlug === m.slug ? "Uploading…" : "Upload new photo"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(m.slug, e.target.files?.[0])} />
              </label>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function GalleryManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    description: "",
    location: "Denver",
    before_caption: "",
    after_caption: "",
    after_caption_2: "",
  });
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [afterFile2, setAfterFile2] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // { id, ...fields }
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/gallery");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const uploadOne = async (file, { watermark = false } = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    if (watermark) fd.append("watermark", "true");
    const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return up.data.url;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!beforeFile && !afterFile && !afterFile2) return toast.error("Please add at least one Before or After photo");
    setSaving(true);
    try {
      let before_url = null;
      let after_url = null;
      let after_url_2 = null;
      if (beforeFile) before_url = await uploadOne(beforeFile);
      // After photos get the Bloomers logo baked into the bottom-right corner by the backend
      if (afterFile) after_url = await uploadOne(afterFile, { watermark: true });
      if (afterFile2) after_url_2 = await uploadOne(afterFile2, { watermark: true });
      await api.post("/gallery", { ...form, variant: "pair", before_url, after_url, after_url_2 });
      setForm({ description: "", location: "Denver", before_caption: "", after_caption: "", after_caption_2: "" });
      setBeforeFile(null);
      setAfterFile(null);
      setAfterFile2(null);
      await load();
      toast.success("Project added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this project?")) return;
    await api.delete(`/gallery/${id}`);
    load();
    toast.success("Deleted");
  };

  const startEdit = (it) => setEditing({
    id: it.id,
    description: it.description || "",
    location: it.location || "Denver",
    before_caption: it.before_caption || "",
    after_caption: it.after_caption || "",
    after_caption_2: it.after_caption_2 || "",
    beforeFile: null,
    afterFile: null,
    afterFile2: null,
  });

  const cancelEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const patch = {
        description: editing.description,
        location: editing.location,
        before_caption: editing.before_caption,
        after_caption: editing.after_caption,
        after_caption_2: editing.after_caption_2,
      };
      if (editing.beforeFile) patch.before_url = await uploadOne(editing.beforeFile);
      if (editing.afterFile) patch.after_url = await uploadOne(editing.afterFile, { watermark: true });
      if (editing.afterFile2) patch.after_url_2 = await uploadOne(editing.afterFile2, { watermark: true });
      await api.patch(`/gallery/${editing.id}`, patch);
      await load();
      setEditing(null);
      toast.success("Project updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-8">
      <form onSubmit={submit} className="card p-6 lg:col-span-1 space-y-4" data-testid="admin-gallery-form">
        <h3 className="text-lg font-black flex items-center gap-2"><ImagePlus className="w-5 h-5 text-[#21C078]" /> Add project</h3>
        <Textarea placeholder="Short description" rows={3} data-testid="admin-gallery-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-2xl" />
        <div className="grid grid-cols-1 gap-3">
          <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
            <SelectTrigger data-testid="admin-gallery-location" className="h-11 rounded-2xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 gap-2">
          <div className="text-xs font-bold uppercase tracking-widest text-[#1C1C1C]/60">Before photo</div>
          <label className="btn-pill-ghost !py-2 !px-4 text-sm cursor-pointer inline-flex" data-testid="admin-gallery-before-file">
            <Upload className="w-4 h-4" /> {beforeFile ? beforeFile.name : "Choose before image"}
            <input type="file" accept="image/*" hidden onChange={(e) => setBeforeFile(e.target.files?.[0] || null)} />
          </label>
          <Input placeholder="Before caption (optional)" data-testid="admin-gallery-before-caption" value={form.before_caption} onChange={(e) => setForm({ ...form, before_caption: e.target.value })} className="h-10 rounded-2xl text-sm" />
        </div>

        <div className="grid grid-cols-1 gap-2">
          <div className="text-xs font-bold uppercase tracking-widest text-[#1C1C1C]/60">After photo #1 <span className="text-[#21C078]">· Bloomers logo added</span></div>
          <label className="btn-pill-ghost !py-2 !px-4 text-sm cursor-pointer inline-flex" data-testid="admin-gallery-after-file">
            <Upload className="w-4 h-4" /> {afterFile ? afterFile.name : "Choose after image"}
            <input type="file" accept="image/*" hidden onChange={(e) => setAfterFile(e.target.files?.[0] || null)} />
          </label>
          <Input placeholder="After #1 caption (optional)" data-testid="admin-gallery-after-caption" value={form.after_caption} onChange={(e) => setForm({ ...form, after_caption: e.target.value })} className="h-10 rounded-2xl text-sm" />
        </div>

        <div className="grid grid-cols-1 gap-2">
          <div className="text-xs font-bold uppercase tracking-widest text-[#1C1C1C]/60">After photo #2 <span className="text-[#21C078]">· Bloomers logo added</span></div>
          <label className="btn-pill-ghost !py-2 !px-4 text-sm cursor-pointer inline-flex" data-testid="admin-gallery-after-file-2">
            <Upload className="w-4 h-4" /> {afterFile2 ? afterFile2.name : "Choose second after image"}
            <input type="file" accept="image/*" hidden onChange={(e) => setAfterFile2(e.target.files?.[0] || null)} />
          </label>
          <Input placeholder="After #2 caption (optional)" data-testid="admin-gallery-after-caption-2" value={form.after_caption_2} onChange={(e) => setForm({ ...form, after_caption_2: e.target.value })} className="h-10 rounded-2xl text-sm" />
        </div>

        <Button type="submit" disabled={saving} data-testid="admin-gallery-submit" className="btn-pill-primary w-full">
          {saving ? "Saving…" : "Add to gallery"}
        </Button>
      </form>

      <div className="lg:col-span-2 grid grid-cols-1 xl:grid-cols-2 gap-6">
        {items.length === 0 && <div className="text-[#1C1C1C]/60">No projects yet.</div>}
        {items.map((it) => (
          <div key={it.id} className="card overflow-hidden" data-testid={`admin-gallery-item-${it.id}`}>
            <div className="grid grid-cols-3 gap-1 bg-white p-1">
              <div className="aspect-[3/4] relative rounded-lg overflow-hidden">
                {it.before_url ? (
                  <img src={absUrl(it.before_url)} alt={`${it.title} — before`} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-xs text-[#1C1C1C]/40 bg-[#F0EBE1]">No before</div>
                )}
                <span className="absolute top-2 left-2 pill-tag bg-black/70 text-white text-[10px] uppercase tracking-widest">Before</span>
              </div>
              <div className="aspect-[3/4] relative rounded-lg overflow-hidden">
                {it.after_url ? (
                  <img src={absUrl(it.after_url)} alt={`${it.title} — after`} className="w-full h-full object-cover" />
                ) : it.image_url ? (
                  <img src={absUrl(it.image_url)} alt={it.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-xs text-[#1C1C1C]/40 bg-[#F0EBE1]">No after 1</div>
                )}
                <span className="absolute top-2 left-2 pill-tag bg-[#21C078] text-white text-[10px] uppercase tracking-widest">After 1</span>
              </div>
              <div className="aspect-[3/4] relative rounded-lg overflow-hidden">
                {it.after_url_2 ? (
                  <img src={absUrl(it.after_url_2)} alt={`${it.title} — after 2`} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-xs text-[#1C1C1C]/40 bg-[#F0EBE1]">No after 2</div>
                )}
                <span className="absolute top-2 left-2 pill-tag bg-[#21C078] text-white text-[10px] uppercase tracking-widest">After 2</span>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="pill-tag bg-[#21C078]/10 text-[#21C078]">{it.location}</span>
              </div>
              {it.description && <div className="text-xs text-[#1C1C1C]/60 line-clamp-2">{it.description}</div>}
              <div className="mt-3 flex items-center gap-3">
                <button onClick={() => startEdit(it)} data-testid={`admin-gallery-edit-${it.id}`} className="text-xs font-bold text-[#219DC0] inline-flex items-center gap-1 hover:underline">
                  Edit
                </button>
                <button onClick={() => del(it.id)} data-testid={`admin-gallery-delete-${it.id}`} className="text-xs font-bold text-[#C12135] inline-flex items-center gap-1 hover:underline">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div
          className="fixed inset-0 bg-black/50 grid place-items-center z-50 p-4"
          data-testid="admin-gallery-edit-modal"
          onClick={cancelEdit}
        >
          <div
            className="bg-white rounded-3xl p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-black">Edit project</h3>
            <Textarea
              placeholder="Short description"
              rows={3}
              data-testid="admin-gallery-edit-desc"
              value={editing.description}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              className="rounded-2xl"
            />
            <div className="grid grid-cols-1 gap-3">
              <Select value={editing.location} onValueChange={(v) => setEditing({ ...editing, location: v })}>
                <SelectTrigger data-testid="admin-gallery-edit-location" className="h-11 rounded-2xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-2">
              <div className="text-xs font-bold uppercase tracking-widest text-[#1C1C1C]/60">Replace before photo (optional)</div>
              <label className="btn-pill-ghost !py-2 !px-4 text-sm cursor-pointer inline-flex" data-testid="admin-gallery-edit-before-file">
                <Upload className="w-4 h-4" /> {editing.beforeFile ? editing.beforeFile.name : "Choose before image"}
                <input type="file" accept="image/*" hidden onChange={(e) => setEditing({ ...editing, beforeFile: e.target.files?.[0] || null })} />
              </label>
              <Input placeholder="Before caption" data-testid="admin-gallery-edit-before-caption" value={editing.before_caption} onChange={(e) => setEditing({ ...editing, before_caption: e.target.value })} className="h-10 rounded-2xl text-sm" />
            </div>
            <div className="grid grid-cols-1 gap-2">
              <div className="text-xs font-bold uppercase tracking-widest text-[#1C1C1C]/60">Replace after photo #1 (optional) <span className="text-[#21C078]">· Bloomers logo added</span></div>
              <label className="btn-pill-ghost !py-2 !px-4 text-sm cursor-pointer inline-flex" data-testid="admin-gallery-edit-after-file">
                <Upload className="w-4 h-4" /> {editing.afterFile ? editing.afterFile.name : "Choose after image"}
                <input type="file" accept="image/*" hidden onChange={(e) => setEditing({ ...editing, afterFile: e.target.files?.[0] || null })} />
              </label>
              <Input placeholder="After #1 caption" data-testid="admin-gallery-edit-after-caption" value={editing.after_caption} onChange={(e) => setEditing({ ...editing, after_caption: e.target.value })} className="h-10 rounded-2xl text-sm" />
            </div>
            <div className="grid grid-cols-1 gap-2">
              <div className="text-xs font-bold uppercase tracking-widest text-[#1C1C1C]/60">Replace after photo #2 (optional) <span className="text-[#21C078]">· Bloomers logo added</span></div>
              <label className="btn-pill-ghost !py-2 !px-4 text-sm cursor-pointer inline-flex" data-testid="admin-gallery-edit-after-file-2">
                <Upload className="w-4 h-4" /> {editing.afterFile2 ? editing.afterFile2.name : "Choose second after image"}
                <input type="file" accept="image/*" hidden onChange={(e) => setEditing({ ...editing, afterFile2: e.target.files?.[0] || null })} />
              </label>
              <Input placeholder="After #2 caption" data-testid="admin-gallery-edit-after-caption-2" value={editing.after_caption_2} onChange={(e) => setEditing({ ...editing, after_caption_2: e.target.value })} className="h-10 rounded-2xl text-sm" />
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button onClick={cancelEdit} className="btn-pill-ghost !py-2 !px-4 text-sm" data-testid="admin-gallery-edit-cancel">Cancel</button>
              <Button onClick={saveEdit} disabled={savingEdit} data-testid="admin-gallery-edit-save" className="btn-pill-primary">
                {savingEdit ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MessagesManager() {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/contact");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const markRead = async (id) => { await api.patch(`/contact/${id}/read`); load(); };
  return (
    <div className="space-y-4">
      {items.length === 0 && <div className="text-[#1C1C1C]/60">No messages yet.</div>}
      {items.map((m) => (
        <div key={m.id} className="card p-5" data-testid={`admin-msg-${m.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <div className="font-black">{m.name}</div>
                {!m.read && <span className="pill-tag bg-[#C12135]/15 text-[#C12135]">New</span>}
              </div>
              <a href={`mailto:${m.email}`} className="text-sm text-[#219DC0] font-bold">{m.email}</a>
              {m.phone && <span className="text-sm text-[#1C1C1C]/60 ml-3">{m.phone}</span>}
              <p className="mt-2 text-[#1C1C1C]/80 whitespace-pre-wrap">{m.message}</p>
              <div className="mt-2 text-xs text-[#1C1C1C]/50">{new Date(m.created_at).toLocaleString()}</div>
            </div>
            {!m.read && (
              <button onClick={() => markRead(m.id)} className="btn-pill-ghost !py-2 !px-4 text-sm" data-testid={`admin-msg-mark-${m.id}`}>
                <CheckCheck className="w-4 h-4" /> Mark read
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function SiteManager() {
  const [carUrl, setCarUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/settings");
    setCarUrl(data?.hero_car_url || null);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("strip_bg", "true");
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/settings", { hero_car_url: up.data.url });
      await load();
      toast.success("Hero Ruby car updated — background stripped and animating on the home page.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const clear = async () => {
    await api.put("/settings", { hero_car_url: null });
    await load();
    toast.success("Hero Ruby car cleared.");
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="card p-6" data-testid="admin-site-hero-car">
        <div className="flex items-center gap-2 text-[#21C078] font-black mb-2">
          <Car className="w-5 h-5" /> Hero — Ruby car illustration
        </div>
        <p className="text-sm text-[#1C1C1C]/70 mb-5">
          Upload the illustrated Ruby car used in the home page hero animation. Recommended: a transparent PNG or WebP (approx. 1200×800). Ruby will animate slowly across the screen to reveal the Bloomers logo behind her.
        </p>
        <div className="w-full aspect-[4/3] rounded-2xl bg-[#F0EBE1] grid place-items-center overflow-hidden mb-5">
          {carUrl ? (
            <img src={absUrl(carUrl)} alt="Hero Ruby car" className="w-full h-full object-contain p-4" />
          ) : (
            <span className="text-[#1C1C1C]/40 text-sm">No image uploaded yet</span>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="btn-pill-primary cursor-pointer inline-flex" data-testid="admin-site-hero-upload">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Uploading…" : (carUrl ? "Replace image" : "Upload image")}
            <input type="file" accept="image/*" hidden onChange={(e) => onFile(e.target.files?.[0])} />
          </label>
          {carUrl && (
            <button onClick={clear} className="btn-pill-ghost" data-testid="admin-site-hero-clear">
              <Trash2 className="w-4 h-4" /> Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  if (loading) return <div className="min-h-[60vh] grid place-items-center text-[#1C1C1C]/60">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="pill-tag bg-[#21C078]/10 text-[#21C078]">Admin</span>
          <h1 className="mt-2 text-4xl font-black tracking-tight">Bloomers dashboard</h1>
          <p className="text-[#1C1C1C]/60">Welcome back, {user.name || user.email}.</p>
        </div>
        <Button onClick={logout} data-testid="admin-logout" className="btn-pill-ghost">Log out</Button>
      </div>

      <Tabs defaultValue="site" className="w-full">
        <TabsList className="bg-[#F0EBE1] rounded-full p-1 mb-8">
          <TabsTrigger value="site" data-testid="admin-tab-site" className="rounded-full data-[state=active]:bg-[#21C078] data-[state=active]:text-white"><Car className="w-4 h-4 mr-1" /> Site</TabsTrigger>
          <TabsTrigger value="team" data-testid="admin-tab-team" className="rounded-full data-[state=active]:bg-[#21C078] data-[state=active]:text-white">Team</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="admin-tab-gallery" className="rounded-full data-[state=active]:bg-[#21C078] data-[state=active]:text-white">Gallery</TabsTrigger>
          <TabsTrigger value="messages" data-testid="admin-tab-messages" className="rounded-full data-[state=active]:bg-[#21C078] data-[state=active]:text-white"><Mail className="w-4 h-4 mr-1" /> Messages</TabsTrigger>
        </TabsList>
        <TabsContent value="site"><SiteManager /></TabsContent>
        <TabsContent value="team"><TeamManager /></TabsContent>
        <TabsContent value="gallery"><GalleryManager /></TabsContent>
        <TabsContent value="messages"><MessagesManager /></TabsContent>
      </Tabs>
    </div>
  );
}
